import React, { useCallback, useEffect, useState } from "react";
import { evaluate, replaceMergeTagsInString } from "../helper";

import Container from "./container";
import { TextInputV2 } from "@boi/react-components";

const calculationFormula = ( formula, cssClass, numberFormat, onChange, props) => () => {
    if (props.calculationFormula) {
        const evalVal = evaluate(formula);
        if (evalVal !== props.value) {
            if (isCurrencyFormat(cssClass, numberFormat) && evalVal.indexOf(".") > -1) {
                if (isIntegerFormat(cssClass) && evalVal.indexOf(".") > -1) {
                    const truncatedValue = evalVal - (evalVal % 1);
                    onChange && onChange(`${truncatedValue}`);
                } else {
                    onChange && onChange(parseFloat(evalVal).toFixed(2));
                }
            } else if (isIntegerFormat(cssClass) && evalVal.indexOf(".") > -1) {
                const truncatedValue = evalVal - (evalVal % 1);
                onChange && onChange(`${truncatedValue}`);
            } else {
                onChange && onChange(evalVal);
            }
        }
    }
};
const getCssClass = (cssClass, isFocused) =>
    `${cssClass}${isFocused === true ? " focus" : ""}`;

const isEuroCurrency = (c, n) =>
    `${c}`.includes("u-currency-euro") ||
    (n === "currency" && !`${c}`.includes("u-currency-sterling"));
const isSterlingCurrency = (c, n) => `${c}`.includes("u-currency-sterling");
const isPercentage = c => `${c}`.includes("u-percentage");
const isIntegerFormat = c => `${c}`.includes("integer-only");
const isCurrencyFormat = (c, n) =>
    isEuroCurrency(c, n) || isSterlingCurrency(c, n);
const getSymbol = (cssClass, numberFormat) =>
    isEuroCurrency(cssClass, numberFormat)
        ? "€"
        : isSterlingCurrency(cssClass, numberFormat)
        ? "£"
        : isPercentage(cssClass)
        ? "%"
        : "";
const keepLeadingZeros = (c) => `${c}`.includes("keep-leading-zeros");
const createOnFocus = (setIsFocused, onFocus) => e => {
    setIsFocused(true);
    onFocus && onFocus(e);
};

// truncate a number without removing any leading zeros.
const truncateNumber = (num) => num.indexOf(".") > -1 ? num.substring(0, num.indexOf(".")) : num;

const createOnChange = (cssClass, numberFormat, onChange) => e => {
    let value = e.target ? e.target.value : e;
    value = value.replaceAll(",", ".");
    const nonNumbers = new RegExp(/[^0-9.]/g);
    value = value.replace(nonNumbers, '');
    if (!keepLeadingZeros(cssClass)) {
        const leadingZeros = new RegExp(
            /^(0+)?([1-9][0-9]*(?:.[0-9]+)?|0(?:.[0-9]+)?)/
        );
        value = value.replace(leadingZeros, "$2");
    }
    const excessDots = new RegExp(/^([^.]*\.)(.*)$/);
    value = value.replace(excessDots, (a, b, c) => b + c.replace(/\./g, ''));
    onChange && onChange(value);
};
const createOnBlur = (
    cssClass,
    numberFormat,
    setIsFocused,
    onChange,
    onBlur
) => e => {
    let value = `${e.target.value}`.trim();
    if (!isIntegerFormat(cssClass)) {
        const closingDot = new RegExp(/[.]+$/g);
        value = value.replace(closingDot, "");
    } else {
        value = truncateNumber(value);
    }

    if (isCurrencyFormat(cssClass, numberFormat) && value.indexOf(".") > -1) {
        onChange && onChange(parseFloat(value).toFixed(2));
    } else {
        onChange && onChange(value);
    }
    
    if (onBlur != null) {
        onBlur(e);
    }
    setIsFocused(false);
};
/* Remove innerRef prop, and add forwardRef to NumberInput component 
if this file has been converted to typescript */
const NumberInput = ({
    onBlur = (value) => {},
    onChange = (value) => {},
    innerRef = (input) => {},
    cssClass = "",
    numberFormat = "",
    showError = true,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const formula = replaceMergeTagsInString(
        props.calculationFormula || "",
        props.context ? props.context.getValues() : {},
        true
    );
    useEffect(calculationFormula(formula, cssClass, numberFormat, onChange, props), [formula]);

    // Remove comma from pre-populate value as it is not supported in number fields
    const { value, allowsPrepopulate, context, name } = props;
    const replacePrepopulateCommas = useCallback(() => {
        if (allowsPrepopulate === true && typeof value === "string" && value) {
            context.setValue(name, value.replace(/,/g, ""));
        }
    }, [value, allowsPrepopulate, context, name]);
    useEffect(replacePrepopulateCommas, []);

    const inputClasses = cssClass
        ?.split(" ")
        .filter(
            (cls) => !cls.includes("u-pl-") && !cls.includes("u-ml-")
        )
        .join(" ");

    return (
        <Container {...props} cssClass={getCssClass(cssClass, isFocused)}>
            <TextInputV2
                placeholder={props.placeholder || ""}
                startSlot={getSymbol(cssClass, numberFormat) ?? null}
                isInvalid={!!props.error && showError}
                onChange={createOnChange(cssClass, numberFormat, onChange)}
                onBlur={
                    createOnBlur(
                        cssClass,
                        numberFormat,
                        setIsFocused,
                        onChange,
                        onBlur
                    )
                }
                onFocus={createOnFocus(setIsFocused, props.onFocus)}
                defaultValue={value}
                value={value}
                name={props.name}
                isDisabled={
                    props.disabled ||
                    props.cssClass?.includes("disabled") ||
                    !!props.field_clone_display
                }
                isReadOnly={
                    props.readOnly || cssClass.includes("readonly")
                }
                isRequired={props.isRequired}
                inputMode={isIntegerFormat(cssClass) ? "numeric" : "decimal"}
                type="text"
                id={"" + props.id}
                className={getCssClass(inputClasses, isFocused) + " react-aria-TextField"}
                min={0}
                step={isIntegerFormat(cssClass) ? "1" : "0.01"}
                // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                aria-label={props.label}
            />
        </Container>
    );
};

export default NumberInput;
export { getSymbol, isEuroCurrency, isSterlingCurrency, isPercentage };